import React, { useState, useEffect, memo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  VStack,
  Text,
  HStack,
} from '@chakra-ui/react';

/**
 * EditContactModal - A standalone modal component for viewing/editing fax contacts
 * 
 * This component manages its own local state to prevent parent re-renders during typing.
 */
const EditContactModal = memo(({
  isOpen,
  onClose,
  contact,
  onSave,
  onDelete,
  isSaving = false,
  isDeleting = false,
}) => {
  // Local form state - completely isolated from parent
  const [formData, setFormData] = useState({
    name: '',
    fax_number: '1',
    business_type: 'other',
    notes: '',
    address: '',
    phone_number: '',
  });

  // Load contact data when modal opens or contact changes
  useEffect(() => {
    if (isOpen && contact) {
      setFormData({
        name: contact.name || '',
        fax_number: contact.fax_number || '1',
        business_type: contact.business_type || 'other',
        notes: contact.notes || '',
        address: contact.address || '',
        phone_number: contact.phone_number || '',
      });
    }
  }, [isOpen, contact]);

  // Simple field update - no debouncing needed since state is local
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (onSave && contact) {
      onSave(contact.id, formData);
    }
  };

  const handleDelete = () => {
    if (onDelete && contact) {
      onDelete(contact.id, contact.name);
    }
  };

  const isValid = formData.name.trim() && formData.fax_number.trim();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Contact</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Contact Name</FormLabel>
              <Input
                placeholder="e.g., CVS Pharmacy - Main St"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel>Fax Number</FormLabel>
              <Input
                placeholder="e.g., 12135551234"
                value={formData.fax_number}
                onChange={(e) => updateField('fax_number', e.target.value)}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Include country code (1 for US). Format will be normalized.
              </Text>
            </FormControl>
            
            <FormControl>
              <FormLabel>Business Type</FormLabel>
              <Select
                value={formData.business_type}
                onChange={(e) => updateField('business_type', e.target.value)}
              >
                <option value="pharmacy">Pharmacy</option>
                <option value="hospital">Hospital</option>
                <option value="clinic">Clinic</option>
                <option value="insurance">Insurance</option>
                <option value="other">Other</option>
              </Select>
            </FormControl>
            
            <FormControl>
              <FormLabel>Phone Number (Optional)</FormLabel>
              <Input
                placeholder="e.g., (213) 555-1234"
                value={formData.phone_number}
                onChange={(e) => updateField('phone_number', e.target.value)}
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>Address (Optional)</FormLabel>
              <Input
                placeholder="e.g., 123 Main St, Los Angeles, CA 90001"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
              />
            </FormControl>
            
            <FormControl>
              <FormLabel>Notes (Optional)</FormLabel>
              <textarea
                placeholder="Any additional notes..."
                value={formData.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                onWheel={(e) => {
                  // Stop propagation to prevent parent modal from capturing scroll
                  e.stopPropagation();
                  const textarea = e.target;
                  const canScrollUp = textarea.scrollTop > 0;
                  const canScrollDown = textarea.scrollTop < (textarea.scrollHeight - textarea.clientHeight);
                  
                  // Only scroll if there's content to scroll
                  if (textarea.scrollHeight > textarea.clientHeight) {
                    if ((e.deltaY < 0 && canScrollUp) || (e.deltaY > 0 && canScrollDown)) {
                      // Let the native scroll happen
                    } else if ((e.deltaY < 0 && !canScrollUp) || (e.deltaY > 0 && !canScrollDown)) {
                      // At bounds, prevent parent scroll
                      e.preventDefault();
                    }
                  }
                }}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  maxHeight: '150px',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #E2E8F0',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  overflowY: 'scroll',
                }}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={2} w="100%" justify="space-between">
            <Button
              colorScheme="red"
              variant="outline"
              onClick={handleDelete}
              isLoading={isDeleting}
              isDisabled={isSaving}
            >
              Delete
            </Button>
            <HStack spacing={2}>
              <Button variant="ghost" onClick={onClose} isDisabled={isSaving || isDeleting}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={!isValid || isDeleting}
              >
                Save Changes
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

EditContactModal.displayName = 'EditContactModal';

export default EditContactModal;
